import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Filter, Plus, Edit3, Trash2, Eye, Download, Upload,
  MoreHorizontal, Users, Calendar, MapPin, Phone, Mail,
  ChevronDown, ChevronLeft, ChevronRight, RefreshCw, Settings,
  UserPlus, Shield, Activity, Clock, CheckCircle, XCircle, FileDown, Key
} from 'lucide-react';
import apiService from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';
import { validateGSTIN, validatePAN, validateIndianMobile, validateEmail } from '../utils/validation';

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // User Type Management
  const [activeTab, setActiveTab] = useState('portal'); // 'portal' or 'customer'

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Filters and Search
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    status: 'all',
    department: 'all',
    dateRange: 'all',
    createdFrom: '',
    createdTo: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales_executive',
    employeeId: '',
    phone: '',
    department: '',
    status: 'active',
    profilePicture: '',
    // Customer-specific fields
    businessName: '',
    businessCategory: '',
    gstNumber: '',
    panNumber: '',
    contactName: '',
    contactRole: '',
    contactEmail: '',
    contactMobile: ''
  });

  const portalRoles = [
    { value: 'admin', label: 'Admin', color: 'bg-red-100 text-red-800' },
    { value: 'sales_executive', label: 'Sales Executive', color: 'bg-blue-100 text-blue-800' },
    { value: 'product_manager', label: 'Product Manager', color: 'bg-green-100 text-green-800' },
    { value: 'order_manager', label: 'Order Manager', color: 'bg-purple-100 text-purple-800' },
    { value: 'reports_manager', label: 'Reports Manager', color: 'bg-indigo-100 text-indigo-800' }
  ];

  const customerStatuses = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'approved', label: 'Approved', color: 'bg-green-100 text-green-800' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'suspended', label: 'Suspended', color: 'bg-gray-100 text-gray-800' },
    { value: 'active', label: 'Customer Manager', color: 'bg-orange-100 text-orange-800' },
    { value: 'inactive', label: 'Inactive Manager', color: 'bg-gray-100 text-gray-800' }
  ];

  const departments = ['Sales', 'Marketing', 'Operations', 'Finance', 'IT', 'HR', 'Management'];
  const businessCategories = ['Retailer', 'Wholesaler', 'Stockist', 'End User', 'Project', 'Distributor'];

  useEffect(() => {
    fetchUsers();
  }, [activeTab]);

  useEffect(() => {
    filterAndSortUsers();
  }, [users, searchTerm, filters, sortConfig]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setTotalPages(Math.ceil(filteredUsers.length / itemsPerPage));
  }, [filteredUsers, currentPage, itemsPerPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let data;

      if (activeTab === 'portal') {
        data = await apiService.getPortalUsers();
      } else {
        data = await apiService.getCustomerUsers();
      }

      setUsers(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setUsers([]);
      setError(`Failed to fetch ${activeTab} users`);
      toast.error(`Failed to fetch ${activeTab} users`);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user => {
        const searchFields = activeTab === 'portal'
          ? [user.name, user.email, user.employeeId, user.department]
          : [user.name, user.email, user.businessName, user.contactName, user.gstNumber];

        return searchFields.some(field =>
          field?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // Role/Status filter
    if (filters.role !== 'all') {
      if (activeTab === 'portal') {
        filtered = filtered.filter(user => user.role === filters.role);
      } else {
        filtered = filtered.filter(user => user.status === filters.role);
      }
    }

    // Status filter (for portal users)
    if (filters.status !== 'all' && activeTab === 'portal') {
      filtered = filtered.filter(user => user.status === filters.status);
    }

    // Department/Category filter
    if (filters.department !== 'all') {
      if (activeTab === 'portal') {
        filtered = filtered.filter(user => user.department === filters.department);
      } else {
        filtered = filtered.filter(user => user.businessCategory === filters.department);
      }
    }

    // Date range filter
    if (filters.createdFrom && filters.createdTo) {
      const fromDate = new Date(filters.createdFrom);
      const toDate = new Date(filters.createdTo);
      filtered = filtered.filter(user => {
        const userDate = new Date(user.createdAt);
        return userDate >= fromDate && userDate <= toDate;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setUsers([]);
    setSelectedUsers([]);
    setCurrentPage(1);
    // Reset filters when switching tabs
    setFilters({
      role: 'all',
      status: 'all',
      department: 'all',
      dateRange: 'all',
      createdFrom: '',
      createdTo: ''
    });
    setSearchTerm('');
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getCurrentUserStats = () => {
    if (activeTab === 'portal') {
      return [
        { label: 'Total Portal Users', value: users.length, icon: Users, color: 'blue' },
        { label: 'Active Users', value: users.filter(u => u.status === 'active').length, icon: CheckCircle, color: 'green' },
        { label: 'Inactive Users', value: users.filter(u => u.status === 'inactive').length, icon: XCircle, color: 'red' },
        { label: 'Admins', value: users.filter(u => u.role === 'admin').length, icon: Shield, color: 'purple' }
      ];
    } else {
      return [
        { label: 'Total Customers', value: users.length, icon: Users, color: 'blue' },
        { label: 'Approved', value: users.filter(u => u.status === 'approved').length, icon: CheckCircle, color: 'green' },
        { label: 'Pending', value: users.filter(u => u.status === 'pending').length, icon: Clock, color: 'yellow' },
        { label: 'Rejected', value: users.filter(u => u.status === 'rejected').length, icon: XCircle, color: 'red' }
      ];
    }
  };

  const getFilterOptions = () => {
    if (activeTab === 'portal') {
      return {
        roleLabel: 'Role',
        roleOptions: portalRoles,
        showStatus: true,
        departmentLabel: 'Department',
        departmentOptions: departments
      };
    } else {
      return {
        roleLabel: 'Status',
        roleOptions: customerStatuses,
        showStatus: false,
        departmentLabel: 'Business Category',
        departmentOptions: businessCategories
      };
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();

    // Validate customer fields if creating customer user
    if (activeTab === 'customer') {
      const errors = {};

      // GST validation
      if (formData.gstNumber) {
        const gstValidation = validateGSTIN(formData.gstNumber);
        if (!gstValidation.isValid) {
          errors.gstNumber = gstValidation.error;
        }
      }

      // PAN validation
      if (formData.panNumber) {
        const panValidation = validatePAN(formData.panNumber);
        if (!panValidation.isValid) {
          errors.panNumber = panValidation.error;
        }
      }

      // Email validation
      if (formData.email) {
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
          errors.email = emailValidation.error;
        }
      }

      if (formData.contactEmail) {
        const contactEmailValidation = validateEmail(formData.contactEmail);
        if (!contactEmailValidation.isValid) {
          errors.contactEmail = contactEmailValidation.error;
        }
      }

      // Mobile validation
      if (formData.contactMobile) {
        const mobileValidation = validateIndianMobile(formData.contactMobile);
        if (!mobileValidation.isValid) {
          errors.contactMobile = mobileValidation.error;
        }
      }

      if (Object.keys(errors).length > 0) {
        Object.values(errors).forEach(error => toast.error(error));
        return;
      }
    }

    try {
      if (activeTab === 'portal') {
        await apiService.createAdmin(formData);
        toast.success('Portal user created successfully');
      } else {
        await apiService.customerRegister(formData);
        toast.success('Customer user created successfully');
      }
      setShowCreateModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to create user');
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();

    // Validate customer fields if editing customer user
    if (activeTab === 'customer') {
      const errors = {};

      // GST validation
      if (formData.gstNumber) {
        const gstValidation = validateGSTIN(formData.gstNumber);
        if (!gstValidation.isValid) {
          errors.gstNumber = gstValidation.error;
        }
      }

      // PAN validation
      if (formData.panNumber) {
        const panValidation = validatePAN(formData.panNumber);
        if (!panValidation.isValid) {
          errors.panNumber = panValidation.error;
        }
      }

      // Email validation
      if (formData.email) {
        const emailValidation = validateEmail(formData.email);
        if (!emailValidation.isValid) {
          errors.email = emailValidation.error;
        }
      }

      if (formData.contactEmail) {
        const contactEmailValidation = validateEmail(formData.contactEmail);
        if (!contactEmailValidation.isValid) {
          errors.contactEmail = contactEmailValidation.error;
        }
      }

      // Mobile validation
      if (formData.contactMobile) {
        const mobileValidation = validateIndianMobile(formData.contactMobile);
        if (!mobileValidation.isValid) {
          errors.contactMobile = mobileValidation.error;
        }
      }

      if (Object.keys(errors).length > 0) {
        Object.values(errors).forEach(error => toast.error(error));
        return;
      }
    }

    try {
      const { password, ...updateData } = formData;
      if (password) updateData.password = password;

      if (activeTab === 'portal') {
        await apiService.updateUser(selectedUser.id, updateData);
        toast.success('Portal user updated successfully');
      } else {
        await apiService.updateCustomer(selectedUser.id, updateData);
        toast.success('Customer user updated successfully');
      }
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      if (activeTab === 'portal') {
        await apiService.deleteUser(selectedUser.id);
        toast.success('Portal user deleted successfully');
      } else {
        await apiService.deleteCustomer(selectedUser.id);
        toast.success('Customer user deleted successfully');
      }
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete user');
    }
  };

  const handleBulkAction = async (action) => {
    try {
      if (activeTab === 'portal') {
        if (action === 'delete') {
          await Promise.all(selectedUsers.map(id => apiService.deleteUser(id)));
          toast.success(`${selectedUsers.length} portal users deleted successfully`);
        } else if (action === 'activate' || action === 'deactivate') {
          const status = action === 'activate' ? 'active' : 'inactive';
          await Promise.all(selectedUsers.map(id => apiService.updateUserStatus(id, status)));
          toast.success(`${selectedUsers.length} portal users ${action}d successfully`);
        }
      } else {
        if (action === 'delete') {
          await Promise.all(selectedUsers.map(id => apiService.deleteCustomer(id)));
          toast.success(`${selectedUsers.length} customers deleted successfully`);
        } else if (action === 'activate' || action === 'deactivate') {
          const status = action === 'activate' ? 'approved' : 'suspended';
          await Promise.all(selectedUsers.map(id => apiService.updateCustomerStatus(id, status)));
          toast.success(`${selectedUsers.length} customers ${action}d successfully`);
        }
      }

      setSelectedUsers([]);
      setShowBulkModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(`Failed to ${action} users`);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      if (activeTab === 'portal') {
        await apiService.updateUserStatus(userId, newStatus);
        toast.success(`Portal user status changed to ${newStatus}`);
      } else {
        await apiService.updateCustomerStatus(userId, newStatus);
        toast.success(`Customer status changed to ${newStatus}`);
      }
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      let result;
      if (activeTab === 'portal') {
        result = await apiService.resetPortalUserPassword(userId);
      } else {
        result = await apiService.resetCustomerPassword(userId);
      }
      toast.success(result.message);
      fetchUsers();
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error('Failed to reset password');
    }
  };

  const resetForm = () => {
    if (activeTab === 'portal') {
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'sales_executive',
        employeeId: '',
        phone: '',
        department: '',
        status: 'active',
        profilePicture: ''
      });
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        businessName: '',
        businessCategory: '',
        gstNumber: '',
        panNumber: '',
        contactName: '',
        contactRole: '',
        contactEmail: '',
        contactMobile: '',
        status: 'pending'
      });
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    if (activeTab === 'portal') {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'sales_executive',
        employeeId: user.employeeId || '',
        phone: user.phone || '',
        department: user.department || '',
        status: user.status || 'active',
        profilePicture: user.profilePicture || ''
      });
    } else {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        businessName: user.businessName || '',
        businessCategory: user.businessCategory || '',
        gstNumber: user.gstNumber || '',
        panNumber: user.panNumber || '',
        contactName: user.contactName || '',
        contactRole: user.contactRole || '',
        contactEmail: user.contactEmail || '',
        contactMobile: user.contactMobile || '',
        status: user.status || 'pending'
      });
    }
    setShowEditModal(true);
  };

  const openViewModal = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAllUsers = () => {
    const currentPageUsers = getCurrentPageUsers();
    const allSelected = currentPageUsers.every(user => selectedUsers.includes(user.id));

    if (allSelected) {
      setSelectedUsers(prev => prev.filter(id => !currentPageUsers.some(user => user.id === id)));
    } else {
      setSelectedUsers(prev => [...prev, ...currentPageUsers.filter(user => !prev.includes(user.id)).map(user => user.id)]);
    }
  };

  const getCurrentPageUsers = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  };

  const exportUsers = () => {
    let headers, rows;

    if (activeTab === 'portal') {
      headers = ['Name', 'Email', 'Role', 'Status', 'Employee ID', 'Department', 'Phone', 'Created At'];
      rows = filteredUsers.map(user => [
        user.name,
        user.email,
        user.role,
        user.status,
        user.employeeId || '',
        user.department || '',
        user.phone || '',
        new Date(user.createdAt).toLocaleDateString()
      ]);
    } else {
      headers = ['Name', 'Email', 'Business Name', 'Status', 'GST Number', 'PAN Number', 'Contact Name', 'Contact Mobile', 'Created At'];
      rows = filteredUsers.map(user => [
        user.name,
        user.email,
        user.businessName || '',
        user.status,
        user.gstNumber || '',
        user.panNumber || '',
        user.contactName || '',
        user.contactMobile || '',
        new Date(user.createdAt).toLocaleDateString()
      ]);
    }

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_users.csv`;
    a.click();
  };

  const exportToCSV = () => {
    const headers = activeTab === 'portal'
      ? ['Name', 'Email', 'Role', 'Status', 'Department', 'Employee ID', 'Phone', 'Created Date']
      : ['Name', 'Email', 'Status', 'Business Name', 'Contact Mobile', 'GST Number', 'Created Date'];

    const csvData = filteredUsers.map(user =>
      activeTab === 'portal'
        ? [
          user.name || '',
          user.email || '',
          user.role || '',
          user.status || '',
          user.department || '',
          user.employeeId || '',
          user.phone || '',
          new Date(user.createdAt).toLocaleDateString()
        ]
        : [
          user.name || '',
          user.email || '',
          user.status || '',
          user.businessName || '',
          user.contactMobile || '',
          user.gstNumber || '',
          new Date(user.createdAt).toLocaleDateString()
        ]
    );

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeTab}_users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Download customer upload template
  const handleDownloadTemplate = async () => {
    try {
      await apiService.downloadCustomerTemplate();
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Template download error:', error);
      toast.error('Failed to download template');
    }
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV file');
      return;
    }

    try {
      setUploading(true);
      const result = await apiService.bulkUploadCustomers(uploadFile);
      setUploadResult(result);
      setShowUploadModal(false);
      setShowUploadResultModal(true);
      setUploadFile(null);

      // Refresh user list
      fetchUsers();

      toast.success(result.message);
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error(error.message || 'Failed to upload customers');
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setUploadFile(file);
      } else {
        toast.error('Please select a CSV file');
        event.target.value = '';
      }
    }
  };

  const getRoleInfo = (roleOrStatus, user = null) => {
    if (activeTab === 'portal') {
      return portalRoles.find(r => r.value === roleOrStatus) || { label: roleOrStatus, color: 'bg-gray-100 text-gray-800' };
    } else {
      // For customer users, handle both regular customers and customer managers
      if (user?.userType === 'customer_manager') {
        // Customer managers use status (active/inactive) 
        return customerStatuses.find(s => s.value === roleOrStatus) || { label: 'Customer Manager', color: 'bg-orange-100 text-orange-800' };
      } else {
        // Regular customers use status (pending/approved/etc.)
        return customerStatuses.find(s => s.value === roleOrStatus) || { label: roleOrStatus, color: 'bg-gray-100 text-gray-800' };
      }
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
            <p className="mt-2 text-gray-600">Manage system users, roles, and permissions</p>
          </div>

          <div className="flex items-center gap-3 mt-4 lg:mt-0">
            <Button
              onClick={exportUsers}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
            {activeTab === 'customer' && (
              <>
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                >
                  <FileDown className="w-4 h-4" />
                  Download Template
                </Button>
                <Button
                  onClick={() => setShowUploadModal(true)}
                  variant="outline"
                  className="flex items-center gap-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Upload className="w-4 h-4" />
                  Bulk Upload
                </Button>
              </>
            )}
            <Button
              onClick={fetchUsers}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              <UserPlus className="w-4 h-4" />
              Add {activeTab === 'portal' ? 'Portal User' : 'Customer'}
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Tab Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => handleTabChange('portal')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${activeTab === 'portal'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <Shield className="w-4 h-4" />
              Portal Users
            </button>
            <button
              onClick={() => handleTabChange('customer')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${activeTab === 'customer'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100'
                }`}
            >
              <Users className="w-4 h-4" />
              Customer Users
            </button>
          </div>

          {/* User Type Description */}
          <div className="flex-1 flex items-center">
            <div className={`px-4 py-2 rounded-lg text-sm ${activeTab === 'portal'
              ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-400'
              : 'bg-green-50 text-green-700 border-l-4 border-green-400'
              }`}>
              {activeTab === 'portal' ? (
                <div>
                  <strong>Portal Users:</strong> Internal staff of your supplier/portal company only - Admin (manages portal operations, approves customers/orders), Sales Executive/Salesman (manages customers), Product/Order/Reports Managers
                </div>
              ) : (
                <div>
                  <strong>Customer Users:</strong> External business customers + their internal managers created during customer registration with multilevel approval (Customer Managers are part of customer company, not portal company)
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
      >
        {getCurrentUserStats().map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-100`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600`} />
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search users by name, email, employee ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Quick Filters */}
          <div className="flex items-center gap-2">
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All {getFilterOptions().roleLabel}s</option>
              {getFilterOptions().roleOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>

            {getFilterOptions().showStatus && (
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            )}

            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              More Filters
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{getFilterOptions().departmentLabel}</label>
                  <select
                    value={filters.department}
                    onChange={(e) => setFilters(prev => ({ ...prev, department: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All {getFilterOptions().departmentLabel}s</option>
                    {getFilterOptions().departmentOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created From</label>
                  <input
                    type="date"
                    value={filters.createdFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, createdFrom: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Created To</label>
                  <input
                    type="date"
                    value={filters.createdTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, createdTo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6"
        >
          <div className="flex items-center justify-between">
            <span className="text-blue-800 font-medium">
              {selectedUsers.length} user(s) selected
            </span>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowBulkModal(true)}
                variant="outline"
                size="sm"
              >
                Bulk Actions
              </Button>
              <Button
                onClick={() => setSelectedUsers([])}
                variant="outline"
                size="sm"
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Users Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
      >
        {error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p>No users found matching your criteria</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left">
                      <input
                        type="checkbox"
                        checked={getCurrentPageUsers().length > 0 && getCurrentPageUsers().every(user => selectedUsers.includes(user.id))}
                        onChange={selectAllUsers}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        User
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('role')}
                    >
                      <div className="flex items-center gap-1">
                        {activeTab === 'portal' ? 'Role' : 'Status'}
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-1">
                        {activeTab === 'portal' ? 'Status' : 'Approval Status'}
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {activeTab === 'portal' ? 'Department' : 'Business Name'}
                    </th>
                    <th
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className="flex items-center gap-1">
                        Created
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {getCurrentPageUsers().map((user, index) => (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.profilePicture ? (
                              <img
                                src={user.profilePicture}
                                alt={user.name}
                                className="h-10 w-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {getInitials(user.name)}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{user.name}</div>
                            <div className="text-sm text-gray-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </div>
                            {user.employeeId && (
                              <div className="text-xs text-gray-400">ID: {user.employeeId}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleInfo(activeTab === 'portal' ? user.role : user.status, user).color}`}>
                          {getRoleInfo(activeTab === 'portal' ? user.role : user.status, user).label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className={`w-2 h-2 rounded-full mr-2 ${activeTab === 'portal'
                            ? (user.status === 'active' ? 'bg-green-400' : 'bg-red-400')
                            : user.userType === 'customer_manager'
                              ? (user.status === 'active' ? 'bg-green-400' : 'bg-red-400')
                              : (user.status === 'approved' || user.status === 'active' ? 'bg-green-400' : user.status === 'pending' ? 'bg-yellow-400' : 'bg-red-400')
                            }`}></div>
                          <span className={`text-sm font-medium ${activeTab === 'portal'
                            ? (user.status === 'active' ? 'text-green-800' : 'text-red-800')
                            : user.userType === 'customer_manager'
                              ? (user.status === 'active' ? 'text-green-800' : 'text-red-800')
                              : (user.status === 'approved' || user.status === 'active' ? 'text-green-800' : user.status === 'pending' ? 'text-yellow-800' : 'text-red-800')
                            }`}>
                            {activeTab === 'portal'
                              ? (user.status === 'active' ? 'Active' : 'Inactive')
                              : user.userType === 'customer_manager'
                                ? (user.status === 'active' ? 'Active' : 'Inactive')
                                : (user.status === 'approved' || user.status === 'active' ? 'Active' : user.status === 'pending' ? 'Pending' : user.status)
                            }
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {activeTab === 'portal'
                          ? (user.department || '-')
                          : user.userType === 'customer_manager'
                            ? (user.relatedCustomer?.businessName || user.businessName || 'Customer Manager')
                            : (user.businessName || '-')
                        }
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(user.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => openViewModal(user)}
                            variant="outline"
                            size="sm"
                            className="p-2"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => openEditModal(user)}
                            variant="outline"
                            size="sm"
                            className="p-2"
                          >
                            <Edit3 className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleResetPassword(user.id)}
                            variant="outline"
                            size="sm"
                            className="p-2 text-orange-600 hover:text-orange-700"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <select
                            value={user.status}
                            onChange={(e) => handleStatusChange(user.id, e.target.value)}
                            className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500"
                          >
                            {activeTab === 'portal' && (
                              <>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </>
                            )}
                            {activeTab === 'customer' && (
                              <>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="suspended">Suspended</option>
                              </>
                            )}
                          </select>
                          <Button
                            onClick={() => openDeleteModal(user)}
                            variant="outline"
                            size="sm"
                            className="p-2 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Show</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="sm"
                    className="p-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  <span className="px-3 py-1 text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="sm"
                    className="p-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredUsers.length)} of {filteredUsers.length} results
                </div>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Create User Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title={`Create New ${activeTab === 'portal' ? 'Portal User' : 'Customer'}`}
        >
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4">
              <Input
                label="Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Input
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {activeTab === 'portal' && portalRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                  {activeTab === 'customer' && (
                    <>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {activeTab === 'portal' && (
                    <>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </>
                  )}
                  {activeTab === 'customer' && (
                    <>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </>
                  )}
                </select>
              </div>
              {activeTab === 'portal' && (
                <>
                  <Input
                    label="Employee ID"
                    type="text"
                    value={formData.employeeId}
                    onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {activeTab === 'customer' && (
                <>
                  <Input
                    label="Business Name"
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    required
                  />
                  <Input
                    label="GST Number"
                    type="text"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                    helperText="15-digit GSTIN (e.g., 22AAAAA0000A1Z5) - Optional"
                    placeholder="22AAAAA0000A1Z5"
                  />
                  <Input
                    label="PAN Number"
                    type="text"
                    value={formData.panNumber}
                    onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                    helperText="10-character PAN (e.g., AAAAA9999A) - Optional"
                    placeholder="AAAAA9999A"
                  />
                  <Input
                    label="Contact Name"
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    required
                  />
                  <Input
                    label="Contact Role"
                    type="text"
                    value={formData.contactRole}
                    onChange={(e) => setFormData({ ...formData, contactRole: e.target.value })}
                  />
                  <Input
                    label="Contact Email"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  />
                  <Input
                    label="Contact Mobile"
                    type="tel"
                    value={formData.contactMobile}
                    onChange={(e) => setFormData({ ...formData, contactMobile: e.target.value })}
                  />
                </>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Create User
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title={`Edit ${activeTab === 'portal' ? 'Portal User' : 'Customer'}`}
        >
          <form onSubmit={handleEditUser}>
            <div className="space-y-4">
              <Input
                label="Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
              <Input
                label="Password (leave blank to keep current)"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {activeTab === 'portal' && portalRoles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                  {activeTab === 'customer' && (
                    <>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {activeTab === 'portal' && (
                    <>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </>
                  )}
                  {activeTab === 'customer' && (
                    <>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </>
                  )}
                </select>
              </div>
              {activeTab === 'portal' && (
                <>
                  <Input
                    label="Employee ID"
                    type="text"
                    value={formData.employeeId}
                    onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Department</option>
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {activeTab === 'customer' && (
                <>
                  <Input
                    label="Business Name"
                    type="text"
                    value={formData.businessName}
                    onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                    required
                  />
                  <Input
                    label="GST Number"
                    type="text"
                    value={formData.gstNumber}
                    onChange={(e) => setFormData({ ...formData, gstNumber: e.target.value })}
                    helperText="15-digit GSTIN (e.g., 22AAAAA0000A1Z5) - Optional"
                    placeholder="22AAAAA0000A1Z5"
                  />
                  <Input
                    label="PAN Number"
                    type="text"
                    value={formData.panNumber}
                    onChange={(e) => setFormData({ ...formData, panNumber: e.target.value })}
                    helperText="10-character PAN (e.g., AAAAA9999A) - Optional"
                    placeholder="AAAAA9999A"
                  />
                  <Input
                    label="Contact Name"
                    type="text"
                    value={formData.contactName}
                    onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                    required
                  />
                  <Input
                    label="Contact Role"
                    type="text"
                    value={formData.contactRole}
                    onChange={(e) => setFormData({ ...formData, contactRole: e.target.value })}
                  />
                  <Input
                    label="Contact Email"
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                  />
                  <Input
                    label="Contact Mobile"
                    type="tel"
                    value={formData.contactMobile}
                    onChange={(e) => setFormData({ ...formData, contactMobile: e.target.value })}
                  />
                </>
              )}
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                Update User
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {/* View User Modal */}
      {showViewModal && selectedUser && (
        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          title={`${activeTab === 'portal' ? 'Portal User' : 'Customer'} Details`}
          size="lg"
        >
          <div className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 h-20 w-20">
                {selectedUser.profilePicture ? (
                  <img
                    src={selectedUser.profilePicture}
                    alt={selectedUser.name}
                    className="h-20 w-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {getInitials(selectedUser.name)}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedUser.name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span>{selectedUser.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{selectedUser.phone || 'Not provided'}</span>
                  </div>
                  {activeTab === 'portal' && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{selectedUser.department || 'Not assigned'}</span>
                    </div>
                  )}
                  {activeTab === 'customer' && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{selectedUser.businessName || 'Not provided'}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Activity className="w-4 h-4 text-gray-400" />
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleInfo(selectedUser.role, selectedUser).color}`}>
                      {getRoleInfo(selectedUser.role, selectedUser).label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>Joined {new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${selectedUser.status === 'active' ? 'bg-green-400' : 'bg-red-400'}`}></div>
                    <span className={`font-medium ${selectedUser.status === 'active' ? 'text-green-800' : 'text-red-800'}`}>
                      {selectedUser.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {activeTab === 'portal' && selectedUser.employeeId && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Employee Information</h4>
                <p className="text-sm text-gray-600">Employee ID: {selectedUser.employeeId}</p>
              </div>
            )}
            {activeTab === 'customer' && (
              <>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Business Information</h4>
                  <p className="text-sm text-gray-600">Business Name: {selectedUser.businessName}</p>
                  <p className="text-sm text-gray-600">GST Number: {selectedUser.gstNumber || 'Not provided'}</p>
                  <p className="text-sm text-gray-600">PAN Number: {selectedUser.panNumber || 'Not provided'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 mt-4">
                  <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                  <p className="text-sm text-gray-600">Contact Name: {selectedUser.contactName}</p>
                  <p className="text-sm text-gray-600">Contact Role: {selectedUser.contactRole || 'Not provided'}</p>
                  <p className="text-sm text-gray-600">Contact Email: {selectedUser.contactEmail || 'Not provided'}</p>
                  <p className="text-sm text-gray-600">Contact Mobile: {selectedUser.contactMobile || 'Not provided'}</p>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Delete User Modal */}
      {showDeleteModal && selectedUser && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Deletion"
        >
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete user <strong>"{selectedUser.name}"</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete User
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Actions Modal */}
      {showBulkModal && (
        <Modal
          isOpen={showBulkModal}
          onClose={() => setShowBulkModal(false)}
          title="Bulk Actions"
        >
          <div className="space-y-4">
            <p className="text-gray-700 mb-4">
              You have selected {selectedUsers.length} user(s). Choose an action:
            </p>
            <div className="space-y-2">
              <Button
                onClick={() => handleBulkAction('activate')}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                Activate Selected Users
              </Button>
              <Button
                onClick={() => handleBulkAction('deactivate')}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                Deactivate Selected Users
              </Button>
              <Button
                onClick={() => handleBulkAction('delete')}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Selected Users
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Upload Modal */}
      {showUploadModal && (
        <Modal
          isOpen={showUploadModal}
          onClose={() => {
            setShowUploadModal(false);
            setUploadFile(null);
          }}
          title="Bulk Upload Customers"
        >
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">📋 Instructions:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Download the template CSV file first</li>
                <li>• Fill in customer data following the template format</li>
                <li>• Required fields: name, email, businessName</li>
                <li>• All uploaded customers will have status "Pending"</li>
                <li>• Default password: "Welcome123!" (customers must change on first login)</li>
              </ul>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {uploadFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Selected: {uploadFile.name}
                  </p>
                )}
              </div>

              <div className="flex justify-between items-center">
                <Button
                  onClick={handleDownloadTemplate}
                  variant="outline"
                  className="flex items-center gap-2 border-green-300 text-green-700 hover:bg-green-50"
                >
                  <FileDown className="w-4 h-4" />
                  Download Template
                </Button>

                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setShowUploadModal(false);
                      setUploadFile(null);
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleBulkUpload}
                    disabled={!uploadFile || uploading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {uploading ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Customers
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Upload Results Modal */}
      {showUploadResultModal && uploadResult && (
        <Modal
          isOpen={showUploadResultModal}
          onClose={() => {
            setShowUploadResultModal(false);
            setUploadResult(null);
          }}
          title="Upload Results"
        >
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{uploadResult.summary.totalRows}</div>
                <div className="text-sm text-blue-800">Total Rows</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{uploadResult.summary.successful}</div>
                <div className="text-sm text-green-800">Successful</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{uploadResult.summary.failed}</div>
                <div className="text-sm text-red-800">Failed</div>
              </div>
            </div>

            {/* Successful uploads */}
            {uploadResult.successfulUploads.length > 0 && (
              <div>
                <h4 className="font-medium text-green-800 mb-3">✅ Successfully Created ({uploadResult.successfulUploads.length})</h4>
                <div className="max-h-40 overflow-y-auto bg-green-50 rounded-lg p-3">
                  {uploadResult.successfulUploads.map((upload, index) => (
                    <div key={index} className="text-sm text-green-700 py-1">
                      Line {upload.line}: {upload.name} ({upload.email})
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed uploads */}
            {uploadResult.failedUploads.length > 0 && (
              <div>
                <h4 className="font-medium text-red-800 mb-3">❌ Failed to Create ({uploadResult.failedUploads.length})</h4>
                <div className="max-h-40 overflow-y-auto bg-red-50 rounded-lg p-3">
                  {uploadResult.failedUploads.map((failure, index) => (
                    <div key={index} className="text-sm text-red-700 py-1">
                      <strong>Line {failure.line}:</strong> {failure.email} - {failure.error}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowUploadResultModal(false);
                  setUploadResult(null);
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Close
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
} 